import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { format } from "date-fns";
import {
  Tabs,
  Tab,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Pagination,
  PaginationItem,
  Tooltip,
} from "@mui/material";
import { useChangeStatusQuery } from "../../Services/settingsAPIs/CommonAPI";
import ChangeStatusModal from "../../components/ticket/ChangeStatusModal";
import ReactModal from "react-modal";
import { useAuditTrailQuery } from "../../Services/ticket/Audit-Trail";
import {
  getIconClasses,
  getBadgeClasses,
  getPriorityFlagColor,
} from "../../components/reusableComponent/IconAndBadgeClasses";
import {
  capitalizeAndFormat,
  capitalizeFirstLetter,
} from "../../components/reusableComponent/CapitalLetter";
import ConfirmationModal from "../../components/reusableComponent/NonActionableConfirmationModal";
import "./TicketListView.scss";

interface IProps {
  ticket: any;
  currentPage: any;
  setCurrentPage: any;
  selectedTickets: any;
  setSelectedTickets: any;
  ticketWorkflow: any;
  value: any;
  setValue: any;
  setStoreKey: any;
  setLiftUiData: any;
  refetch: any;
  storingTicketData: any;
  useData: any;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  onPageChange?: (page: number, size: number) => void;
  isTicketLoading: any;
  onNonActionable?: (ticketId: string | number, currentValue: boolean) => void;
}

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "450px",
  },
};

const TicketListView: React.FC<IProps> = ({
  ticket,
  currentPage,
  setCurrentPage,
  selectedTickets,
  setSelectedTickets,
  ticketWorkflow,
  value,
  setValue,
  setStoreKey,
  setLiftUiData,
  refetch,
  storingTicketData,
  useData,
  pageSize: propPageSize,
  setPageSize: propSetPageSize,
  onPageChange,
  isTicketLoading,
  onNonActionable,
}) => {
  const ticketCount = ticket?.count ? ticket?.count : 0;
  const [selectAll, setSelectAll] = useState(false);
  const params = useParams();
  const uuid = params.uuid;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ticketListRef = useRef<HTMLDivElement>(null);
  const ticketRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  const [internalPageSize, setInternalPageSize] = useState<number>(
    propPageSize || 50,
  );
  const pageSize = propPageSize || internalPageSize;
  const setPageSize = propSetPageSize || setInternalPageSize;

  const { data: auditTrailData, refetch: refetchAuditTrailData } =
    useAuditTrailQuery(uuid, { skip: !uuid });

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [originalStatus, setOriginalStatus] = useState("");
  const [selectedAllStatus, setAllSelectedStatus] = useState("");
  const [isStatusModalOpen, setStatusModalOpen] = useState(false);
  const [storingTicketId, setStoringTicketId] = useState("");

  const { data: ticketData, refetch: refetchAuditTrail } = useChangeStatusQuery(
    ticket?.results?.[0]?.action?.disposition?.id,
    { skip: !ticket?.results?.[0]?.action?.disposition?.id },
  );

  const [tabLoading, setTabLoading] = useState<boolean>(false);

  // ✅ Confirmation modal state — lifted outside the map so only one modal exists
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{
    ticketId: string | number;
    currentValue: boolean;
  } | null>(null);

  const storeScrollPosition = (ticketId: string) => {
    const el = scrollContainerRef.current;
    if (el)
      sessionStorage.setItem(
        `ticketListScrollPosition_${value}`,
        String(el.scrollTop),
      );
    sessionStorage.setItem(`lastViewedTicket_${value}`, ticketId);
  };
  const navigate = useNavigate();
  const restoreScrollPosition = () => {
    const saved = sessionStorage.getItem(`ticketListScrollPosition_${value}`);
    if (!saved) return () => {};
    const target = parseInt(saved, 10);
    if (Number.isNaN(target) || target < 0) return () => {};
    const apply = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = target;
    };
    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(apply, d));
    requestAnimationFrame(() => requestAnimationFrame(apply));
    return () => timeouts.forEach((t) => clearTimeout(t));
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(`ticketListScrollPosition_${value}`);
    sessionStorage.removeItem(`lastViewedTicket_${value}`);
  };

  const openStatusModal = () => {
    setOriginalStatus(selectedStatus);
    setStatusModalOpen(true);
  };
  const closeStatusModal = () => {
    setSelectedStatus(originalStatus);
    setStatusModalOpen(false);
  };

  const handlerStatus = (allStatus) => {
    setAllSelectedStatus(allStatus);
    setSelectedStatus(allStatus.status_type);
    openStatusModal();
  };

  const formatStatusData = (status) => {
    if (typeof status.status === "string") {
      return { status_type: status.status_type, status: status.status };
    }
    return {
      id: status?.status?.id,
      status_type: status?.status_type,
      status: status?.status?.status,
    };
  };
  //  comments
  const tenantName = window.location.hostname.split(".")[0]?.toLowerCase();

  const statusOptions = ticketData?.all_statuses
    ?.map((status) => formatStatusData(status))
    ?.filter((status) => {
      if (
        (tenantName === "payswiffibl" || tenantName === "payswiff") &&
        status.status_type === "reopen"
      )
        return false;
      return true;
    });

  useEffect(() => {
    const defaultSelections = ticket?.results?.reduce(
      (acc: any, status: any) => {
        acc[status?.id] = status?.status_display || "";
        return acc;
      },
      {},
    );
    setSelectedStatus(defaultSelections || {});
  }, [ticket]);

  const handleTicketSelect = (ticketId: string) => {
    setSelectedTickets((prev) => {
      if (prev.includes(ticketId)) return prev.filter((id) => id !== ticketId);
      return [...prev, ticketId];
    });
  };

  const totalPages = Math.ceil(ticketCount / pageSize);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 3000);
  }, [currentPage]);

  useEffect(() => {
    const canRestore =
      !loading && !isTicketLoading && ticket?.results?.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function")
          scrollRestoreCleanupRef.current = cleanup;
      }, 50);
      return () => {
        clearTimeout(timer);
        scrollRestoreCleanupRef.current?.();
        scrollRestoreCleanupRef.current = null;
      };
    }
  }, [loading, isTicketLoading, ticket?.results, value]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const el = scrollContainerRef.current;
      if (el)
        sessionStorage.setItem(
          `ticketListScrollPosition_${value}`,
          String(el.scrollTop),
        );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [value]);

  const formatDate = (dateString: string) =>
    format(new Date(dateString), "dd-MM-yyyy HH:mm:ss");

  const getStatusStyle = (status_type: string) => {
    switch (status_type) {
      case "active":
        return "status-open";
      case "closed":
        return "status-closed";
      case "reopen":
        return "status-reopen";
      case "wip":
        return "status-wip";
      default:
        return "status-default";
    }
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "priority-high";
      case "medium":
        return "priority-medium";
      case "low":
        return "priority-low";
      default:
        return "priority-default";
    }
  };

  const truncateDescription = (description, maxLength) => {
    if (description?.length > maxLength)
      return description?.slice(0, maxLength) + "...";
    return description;
  };

  const [uiData, setUiData] = useState([]);
  const [keyState, setKeyState] = useState([]);

  const filteredUiData = uiData.filter((item) => {
    if (
      (tenantName === "meta" || tenantName === "payswiffibl") &&
      item.key.toLowerCase() === "reopen"
    )
      return false;
    return true;
  });

  const processData = async () => {
    const uiFormattedData = [];
    const keysForState = [];
    if (ticketWorkflow && typeof ticketWorkflow === "object") {
      await Object.entries(ticketWorkflow)?.forEach(([key, value]) => {
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          Object.entries(value).forEach(([nk, nv]) => {
            uiFormattedData.push({ key: nk, value: nv });
            keysForState.push(key);
          });
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item && typeof item === "object") {
              Object.entries(item).forEach(([nk, nv]) => {
                uiFormattedData.push({ key: nk, value: nv });
                keysForState.push(key);
              });
            }
          });
        }
      });
      setUiData(uiFormattedData);
      setKeyState(keysForState);
    } else {
      console.error("ticketWorkflow is undefined or null");
    }
  };

  const handleStatusUpdate = () => setStatusModalOpen(false);

  useEffect(() => {
    processData();
  }, [ticketWorkflow]);
  useEffect(() => {
    setLiftUiData(uiData);
  }, [uiData]);
  useEffect(() => {
    setTabLoading(true);
    setTimeout(() => setTabLoading(false), 3000);
  }, [value]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 120 },
    },
  };

  const handleStatusChangeeee = (ticketId) => setStoringTicketId(ticketId?.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedTickets(ticket?.results.map((row) => row.id));
    } else {
      setSelectedTickets([]);
    }
  };

  useEffect(() => {
    if (
      selectedTickets.length === ticket?.results?.length &&
      ticket?.results?.length > 0
    )
      setSelectAll(true);
    else setSelectAll(false);
  }, [selectedTickets]);

  const pageSizeOptions = [25, 50, 100, 200];

  const handlePageSizeChange = (event: any) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    setCurrentPage(1);
    sessionStorage.setItem("ticketListViewPageSize", newPageSize.toString());
    clearScrollPosition();
    onPageChange?.(1, newPageSize);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    clearScrollPosition();
    onPageChange?.(page, pageSize);
  };

  const handleTicketClick = (ticketId: string) => {
    storeScrollPosition(ticketId);
    navigate(`/ticket/information/${ticketId}`);
  };

  // ✅ Handler that fires after the user clicks "Confirm" in the modal
  const handleConfirmNonActionable = () => {
    if (confirmPayload && onNonActionable) {
      onNonActionable(confirmPayload.ticketId, confirmPayload.currentValue);
    }
    setConfirmPayload(null);
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Tabs Header */}
          <div className="tabs-header-section">
            <div className="tabs-header-content mx-5">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="select-all-checkbox"
              />
              <Tabs
                value={filteredUiData.findIndex((item) => item.key === value)}
                onChange={(event, newValue) => {
                  clearScrollPosition();
                  setStoreKey(keyState[newValue]);
                  setValue(filteredUiData[newValue].key);
                  setCurrentPage(1);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: "44px",
                  backgroundColor: "#ffffff",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#6366f1",
                    height: "2px",
                  },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    minHeight: "44px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6c757d",
                    padding: "10px 20px",
                    transition: "all 0.2s ease",
                    minWidth: "auto",
                    "&:hover": { color: "#495057", backgroundColor: "#f8f9fa" },
                    "&.Mui-selected": { color: "#6366f1", fontWeight: 600 },
                  },
                  "& .MuiTabs-scrollButtons": {
                    color: "#6c757d",
                    "&.Mui-disabled": { opacity: 0.3 },
                  },
                }}
              >
                {filteredUiData.map((ticketsInfo, index) => (
                  <Tooltip
                    key={index}
                    title={`${ticketsInfo.key} (${ticketsInfo.value})`}
                    arrow
                  >
                    <Tab
                      key={index}
                      label={truncateDescription(
                        `${ticketsInfo.key} (${ticketsInfo.value})`,
                        25,
                      )}
                    />
                  </Tooltip>
                ))}
              </Tabs>
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            ref={scrollContainerRef}
            className="ticket-list-scroll-container"
          >
            {isTicketLoading ? (
              <div className="loading-container">
                <LoadingSpinner />
              </div>
            ) : !ticket?.results || ticket?.results?.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox empty-icon"></i>
                <h5 className="empty-title">No tickets available</h5>
                <p className="empty-description">
                  No tickets found for the current selection. Try adjusting your
                  filters or workflow selection.
                </p>
              </div>
            ) : (
              <div className="modern-tickets-container">
                {ticket?.results?.map((ticketItem: any) => {
                  return (
                    <div
                      key={ticketItem?.id}
                      ref={(el) => (ticketRefs.current[ticketItem?.id] = el)}
                      className="modern-ticket-card"
                    >
                      {/* Checkbox */}
                      <div className="card-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTickets.includes(ticketItem?.id)}
                          onChange={() => handleTicketSelect(ticketItem?.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Card Header */}
                      <div className="card-header-section">
                        <div className="header-left">
                          <span className="ticket-ref-id">
                            {ticketItem?.id}
                          </span>
                          <div
                            className={`channel-badge-wrapper ${getBadgeClasses(
                              ticketItem?.ticket_channel,
                            )}`}
                            title={ticketItem?.ticket_channel}
                          >
                            <i
                              className={`${getIconClasses(
                                ticketItem?.ticket_channel,
                              )}`}
                            ></i>
                          </div>
                          <span className="channel-label">
                            {capitalizeFirstLetter(
                              ticketItem?.ticket_channel || "",
                            )}
                          </span>
                          {ticketItem.attachments?.length > 0 && (
                            <Tooltip
                              title={`${ticketItem.attachments.length} attachment(s)`}
                            >
                              <i className="bi bi-paperclip attachment-icon"></i>
                            </Tooltip>
                          )}
                        </div>
                        <div className="header-right">
                          {/* ── Non‑actionable toggle — opens shared ConfirmationModal ── */}
                          {onNonActionable && (
                            <Tooltip
                              title={
                                ticketItem?.is_non_actionable
                                  ? "Mark as actionable"
                                  : "Mark as non‑actionable"
                              }
                            >
                              <button
                                className="action-icon-btn"
                                onClickCapture={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setConfirmPayload({
                                    ticketId: ticketItem?.id,
                                    currentValue: ticketItem?.is_non_actionable,
                                  });
                                  setConfirmModalOpen(true);
                                }}
                                aria-label={
                                  ticketItem?.is_non_actionable
                                    ? "Mark as actionable"
                                    : "Mark as non‑actionable"
                                }
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                }}
                              >
                                <i
                                  className={`bi ${
                                    ticketItem?.is_non_actionable
                                      ? "bi-ticket-perforated-fill fs-2"
                                      : "bi-ticket-perforated fs-2"
                                  }`}
                                  style={{
                                    color: ticketItem?.is_non_actionable
                                      ? "#059669"
                                      : "#6B7280",
                                    fontSize: "15px",
                                  }}
                                />
                              </button>
                            </Tooltip>
                          )}

                          <span
                            className={`priority-pill ${getPriorityStyle(
                              ticketItem?.action?.disposition?.priority,
                            )}`}
                            onClickCapture={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            {ticketItem?.action?.disposition?.priority?.toUpperCase() ||
                              "LOW"}
                          </span>
                          <Tooltip
                            title={`${capitalizeFirstLetter(
                              ticketItem?.action?.disposition?.priority,
                            )} Priority`}
                          >
                            <i
                              className="bi bi-flag-fill priority-flag"
                              onClickCapture={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              style={{
                                color: getPriorityFlagColor(
                                  ticketItem?.action?.disposition?.priority,
                                ),
                              }}
                            ></i>
                          </Tooltip>
                        </div>
                      </div>

                      {/* Card Body */}
                      <Link
                        to={`/ticket/information/${ticketItem?.id}`}
                        onClick={() => handleTicketClick(ticketItem?.id)}
                        className="card-body-link"
                      >
                        <div className="card-body-section">
                          <h3 className="ticket-main-title">
                            {ticketItem?.title}
                          </h3>
                          <p className="ticket-main-description">
                            {truncateDescription(ticketItem?.description, 150)}
                          </p>
                        </div>
                      </Link>

                      {/* Card Footer */}
                      <Link
                        className="card-footer-section"
                        onClick={() => handleTicketClick(ticketItem?.id)}
                        to={`/ticket/information/${ticketItem?.id}`}
                      >
                        <div className="footer-info-grid">
                          <div className="footer-item">
                            <span className="footer-label">Assignee</span>
                            <div className="footer-value-wrapper">
                              <div className="avatar-circle">
                                {ticketItem?.action?.assignee?.username
                                  ?.charAt(0)
                                  ?.toUpperCase() || "U"}
                              </div>
                              <span className="footer-value">
                                {capitalizeFirstLetter(
                                  ticketItem?.action?.assignee?.username?.split(
                                    "@",
                                  )[0] ?? "Unassigned",
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="footer-item">
                            <span className="footer-label">Customer</span>
                            <span className="footer-value">
                              {ticketItem?.action?.contact?.name
                                ? capitalizeFirstLetter(
                                    ticketItem?.action?.contact.name,
                                  )
                                : "CRM Portal"}
                            </span>
                          </div>
                          <div className="footer-item">
                            <span className="footer-label">Raised by</span>
                            <span className="footer-value">
                              {capitalizeFirstLetter(
                                ticketItem?.created_by?.username ||
                                  ticketItem?.created_by ||
                                  "-",
                              )}
                            </span>
                          </div>
                          <div className="footer-item">
                            <span className="footer-label">Workflow</span>
                            <span className="footer-value">
                              {ticketItem?.action?.disposition?.workflow
                                ?.name || "Service Test"}
                            </span>
                          </div>
                          <div className="footer-item">
                            <span className="footer-label">Current team</span>
                            <span className="footer-value">
                              {ticketItem?.team_assignee?.name ||
                                ticketItem?.team_name ||
                                "-"}
                            </span>
                          </div>
                          <div className="footer-item">
                            <span className="footer-label">Created at</span>
                            <span className="footer-value">
                              {formatDate(ticketItem?.created)}
                            </span>
                          </div>

                          <div className="footer-item">
                            <span className="footer-label">Resolved at</span>
                            <span className="footer-value">
                              {ticketItem?.resolved_at
                                ? formatDate(ticketItem?.resolved_at)
                                : "-"}
                            </span>
                          </div>
                        </div>

                        <div className="footer-actions">
                          {/* {ticketItem?.action.thread ? (
                            <Link
                              to={`/email/inbox/${ticketItem?.action.thread[0]?.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTicketClick(ticketItem?.id);
                              }}
                            >
                              <Tooltip title="Process Email">
                                <button className="action-icon-btn">
                                  <i className="bi bi-chat-left-fill action-icon"></i>
                                </button>
                              </Tooltip>
                            </Link>
                          ) : (
                            <Link
                              to="/email/compose"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTicketClick(ticketItem?.id);
                              }}
                            >
                              <Tooltip title="Compose Email">
                                <button className="action-icon-btn">
                                  <i className="bi bi-pencil-square action-icon"></i>
                                </button>
                              </Tooltip>
                            </Link>
                          )} */}

                          <div
                            className="status-change-wrapper"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FormControl
                              className="status-select-control"
                              size="small"
                            >
                              <InputLabel
                                id={`status-select-label-${ticketItem.id}`}
                              >
                                Change status
                              </InputLabel>
                              <Select
                                labelId={`status-select-label-${ticketItem.id}`}
                                id={`status-select-${ticketItem.id}`}
                                value={selectedStatus[ticketItem.id] || ""}
                                onChange={() =>
                                  handleStatusChangeeee(ticketItem)
                                }
                                label="Change Status"
                                MenuProps={MenuProps}
                              >
                                {statusOptions?.map((status: any) => (
                                  <MenuItem
                                    key={status.id}
                                    value={status.status}
                                    onClick={() => handlerStatus(status)}
                                    title={status?.status}
                                  >
                                    {truncateDescription(status?.status, 25)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="email-pagination">
            <div className="email-pagination__left">
              <span className="email-pagination__label">Show</span>
              <select
                className="email-pagination__select"
                value={pageSize}
                onChange={handlePageSizeChange}
                aria-label="Select page size"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="email-pagination__label">per page</span>
            </div>
            <div className="email-pagination__right">
              {totalPages > 0 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  renderItem={(item) => <PaginationItem {...item} />}
                />
              )}
            </div>
          </div>

          {/* Status Modal */}
          <ReactModal
            isOpen={isStatusModalOpen}
            onRequestClose={closeStatusModal}
            style={customModalStyles}
            contentLabel="Change Status"
            shouldCloseOnOverlayClick={false}
          >
            <ChangeStatusModal
              data={ticketData}
              closeStatusModal={closeStatusModal}
              refetch={refetch}
              refetchAuditTrail={refetchAuditTrail}
              selectedStatus={selectedStatus}
              handleStatusUpdate={handleStatusUpdate}
              refetchAuditTrailData={refetchAuditTrailData}
              selectedAllStatus={selectedAllStatus}
              storingTicketId={storingTicketId}
              refetchAuditTrailForwardedData={refetchAuditTrailData}
            />
          </ReactModal>

          {/* ✅ Single reusable ConfirmationModal — rendered once, outside the map */}
          <ConfirmationModal
            isOpen={confirmModalOpen}
            onClose={() => {
              setConfirmModalOpen(false);
              setConfirmPayload(null);
            }}
            onConfirm={handleConfirmNonActionable}
            title="Confirm Action"
            message={
              <>
                Are you sure you want to{" "}
                <strong>
                  {confirmPayload?.currentValue
                    ? "mark as actionable"
                    : "mark as non‑actionable"}
                </strong>
                ?
              </>
            }
          />
        </>
      )}
    </>
  );
};

export default TicketListView;
